/**
 * PaymentGatewaySelector - Component for selecting payment gateway during registration
 * Supports Stripe checkout and custom payment gateways
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CreditCard, Building2, Smartphone, Banknote, Wallet,
  Loader2, CheckCircle2, Copy, ExternalLink, DollarSign,
  AlertCircle, ChevronRight, ChevronLeft, Check, Calendar, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { customPaymentAPI, paymentMethodsAPI, paymentAPI } from '@/lib/api';

// Gateway icons mapping
const gatewayIcons = {
  manual: DollarSign,
  stripe: CreditCard,
  paypal: Wallet,
  razorpay: CreditCard,
  bank_transfer: Building2,
  ecocash: Smartphone,
  mobile_money: Smartphone,
  crypto: Wallet,
};

// Currency symbols
const currencySymbols = {
  USD: '$',
  ZWL: 'Z$',
  ZAR: 'R',
  GBP: '£',
  EUR: '€',
  KES: 'KSh',
  NGN: '₦',
};

/**
 * Payment Gateway Selector Component
 */
export const PaymentGatewaySelector = ({ 
  eventId, 
  delegateId,
  tierId,
  tierName,
  delegateEmail,
  delegateName,
  amount, 
  currency = 'USD',
  onPaymentInitialized,
  onPaymentComplete,
  onCancel 
}) => {
  const [step, setStep] = useState(1); // 1: select gateway, 2: payment details, 3: instructions
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [paymentType, setPaymentType] = useState('one_time');
  const [installmentCount, setInstallmentCount] = useState(3);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(currency);
  const [paymentResult, setPaymentResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch available gateways
  useEffect(() => {
    const fetchGateways = async () => {
      try {
        // Start with Stripe as the primary option
        let allGateways = [
          {
            id: 'stripe',
            name: 'Credit/Debit Card',
            gateway: 'stripe',
            is_active: true,
            description: 'Pay securely with Visa, Mastercard, or Amex',
            icon: 'card',
          }
        ];
        
        // Try the payment methods API to add more options
        try {
          const methodsResponse = await paymentMethodsAPI.getPublicMethods(eventId);
          const methods = methodsResponse.data?.payment_methods || [];
          // Convert to gateway format and add to list
          const customMethods = methods.map(m => ({
            id: m.id,
            name: m.name,
            gateway: m.type,
            is_active: true,
            instructions: m.instructions,
            fields: m.fields || [],
            icon: m.icon,
          }));
          allGateways = [...allGateways, ...customMethods];
        } catch (e) {
          console.log('Custom payment methods not available');
        }
        
        setGateways(allGateways);
        
        // Auto-select Stripe (first gateway)
        if (allGateways.length > 0) {
          setSelectedGateway(allGateways[0]);
        }
      } catch (err) {
        console.error('Failed to fetch gateways:', err);
        setError('Failed to load payment options');
        toast.error('Failed to load payment options');
      } finally {
        setLoading(false);
      }
    };

    fetchGateways();
  }, [eventId]);

  // Calculate fees
  const calculateFees = () => {
    if (!selectedGateway) return { fee: 0, total: amount };
    const feePercent = parseFloat(selectedGateway.fee_percentage) || 0;
    const feeFixed = parseFloat(selectedGateway.fee_fixed) || 0;
    const fee = (amount * feePercent / 100) + feeFixed;
    return { fee: fee.toFixed(2), total: (amount + fee).toFixed(2) };
  };

  // Calculate installment amount
  const calculateInstallment = () => {
    const { total } = calculateFees();
    return (parseFloat(total) / installmentCount).toFixed(2);
  };

  // Initialize payment
  const initializePayment = async () => {
    if (!selectedGateway) {
      toast.error('Please select a payment method');
      return;
    }

    // Validate phone for EcoCash
    if (selectedGateway.gateway === 'ecocash' && !phoneNumber) {
      setError('Phone number is required for EcoCash payments');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Handle Stripe checkout separately
      if (selectedGateway.gateway === 'stripe') {
        // The checkout route identifies the payer by delegate_id — it looks the
        // email and the tier price up itself. Sending delegate_email/name and
        // no delegate_id was a 422 on every attempt.
        if (!delegateId) {
          setError('Finish registering before paying — we need your delegate record first.');
          setProcessing(false);
          return;
        }
        const originUrl = window.location.origin;
        const response = await paymentAPI.createCheckout({
          event_id: eventId,
          delegate_id: delegateId,
          tier_id: tierId,
          origin_url: originUrl
        });

        // The endpoint returns `url`; `checkout_url` is only its stored copy.
        const checkoutUrl = response.data?.url;
        if (checkoutUrl) {
          // Store pending payment info for after redirect
          sessionStorage.setItem('pending_payment', JSON.stringify({
            event_id: eventId,
            tier_id: tierId,
            session_id: response.data.session_id
          }));
          
          // Redirect to Stripe checkout
          window.location.href = checkoutUrl;
          return;
        }
      }
      
      // Handle other payment gateways
      const paymentData = {
        gateway_id: selectedGateway.id,
        amount: amount,
        currency: selectedCurrency,
        delegate_id: delegateId,
        tier_id: tierId,
        payment_type: paymentType,
      };

      // Add installment count if applicable
      if (paymentType === 'installment') {
        paymentData.installment_count = installmentCount;
      }

      // Add phone number for EcoCash
      if (selectedGateway.gateway === 'ecocash') {
        paymentData.phone_number = phoneNumber;
      }

      const response = await customPaymentAPI.initializePayment(eventId, paymentData);
      setPaymentResult(response.data);

      // Handle different gateway flows
      if (selectedGateway.gateway === 'paypal' && response.data.instructions?.approval_url) {
        window.location.href = response.data.instructions.approval_url;
        return;
      }

      // Show instructions for manual/bank/ecocash
      setStep(3);
      onPaymentInitialized?.(response.data);
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.message || 'Payment initialization failed';
      setError(message);
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleComplete = () => {
    onPaymentComplete?.(paymentResult);
  };

  const getCurrencySymbol = (code) => currencySymbols[code] || code;

  // Loading state
  if (loading) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2">Loading payment options...</span>
        </CardContent>
      </Card>
    );
  }

  // No gateways available
  if (gateways.length === 0) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Payment Methods Available</h3>
          <p className="text-gray-500 mb-4">Please contact the event organizer for payment options.</p>
          <Button variant="outline" onClick={onCancel}>Go Back</Button>
        </CardContent>
      </Card>
    );
  }

  const { fee, total } = calculateFees();

  return (
    <Card className="w-full max-w-lg mx-auto" data-testid="payment-gateway-selector">
      <CardHeader>
        <CardTitle>Complete Your Payment</CardTitle>
        <CardDescription>
          {tierName ? `${tierName} - ` : ''}{getCurrencySymbol(selectedCurrency)}{amount.toFixed(2)}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Select Gateway */}
        {step === 1 && (
          <div className="space-y-4">
            <Label className="text-base font-medium">Select Payment Method</Label>
            <div className="grid gap-3">
              {gateways.map((gateway) => {
                const Icon = gatewayIcons[gateway.gateway] || CreditCard;
                const isSelected = selectedGateway?.id === gateway.id;
                
                return (
                  <div
                    key={gateway.id}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-600' : 'hover:border-gray-400'
                    }`}
                    onClick={() => setSelectedGateway(gateway)}
                    data-testid={`gateway-${gateway.gateway}`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="font-medium">{gateway.name}</p>
                      <p className="text-sm text-gray-500">{gateway.description}</p>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                    {(gateway.fee_percentage > 0 || gateway.fee_fixed > 0) && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        {gateway.fee_percentage}% fee
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Payment Details */}
        {step === 2 && selectedGateway && (
          <div className="space-y-6">
            {/* Currency Selection */}
            {selectedGateway.supported_currencies?.length > 1 && (
              <div className="space-y-2">
                <Label>Currency</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedGateway.supported_currencies.map((curr) => (
                    <Button
                      key={curr}
                      variant={selectedCurrency === curr ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCurrency(curr)}
                    >
                      {getCurrencySymbol(curr)} {curr}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Type (if installments supported) */}
            {selectedGateway.supports_installments && (
              <div className="space-y-3">
                <Label>Payment Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`p-3 border rounded-lg cursor-pointer ${paymentType === 'one_time' ? 'border-blue-600 bg-blue-50' : ''}`}
                    onClick={() => setPaymentType('one_time')}
                  >
                    <p className="font-medium">Pay Once</p>
                    <p className="text-sm text-gray-500">Full payment now</p>
                  </div>
                  <div
                    className={`p-3 border rounded-lg cursor-pointer ${paymentType === 'installment' ? 'border-blue-600 bg-blue-50' : ''}`}
                    onClick={() => setPaymentType('installment')}
                  >
                    <p className="font-medium">Installments</p>
                    <p className="text-sm text-gray-500">Split payment</p>
                  </div>
                </div>
              </div>
            )}

            {/* Installment Options */}
            {paymentType === 'installment' && (
              <div className="space-y-2">
                <Label>Number of Payments</Label>
                <div className="flex flex-wrap gap-2">
                  {[2, 3, 4, 6].map((count) => (
                    <Button
                      key={count}
                      variant={installmentCount === count ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setInstallmentCount(count)}
                    >
                      {count} payments
                    </Button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {getCurrencySymbol(selectedCurrency)}{calculateInstallment()} per payment
                </p>
              </div>
            )}

            {/* EcoCash Phone Number */}
            {selectedGateway.gateway === 'ecocash' && (
              <div className="space-y-2">
                <Label htmlFor="phone">EcoCash Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+263 77 123 4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  data-testid="ecocash-phone"
                />
                <p className="text-xs text-gray-500">
                  Enter your EcoCash number to receive the payment prompt
                </p>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>{tierName || 'Registration'}</span>
                <span>{getCurrencySymbol(selectedCurrency)}{amount.toFixed(2)}</span>
              </div>
              {parseFloat(fee) > 0 && (
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Processing Fee</span>
                  <span>{getCurrencySymbol(selectedCurrency)}{fee}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>{getCurrencySymbol(selectedCurrency)}{total}</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment Instructions */}
        {step === 3 && paymentResult && (
          <div className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Payment request created successfully!
              </AlertDescription>
            </Alert>

            {/* Bank Transfer Instructions */}
            {paymentResult.instructions?.type === 'bank_transfer' && (
              <div className="space-y-4">
                <h4 className="font-medium flex items-center">
                  <Building2 className="w-5 h-5 mr-2" />
                  Bank Transfer Details
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div>
                    <Label className="text-gray-500 text-xs">Bank Name</Label>
                    <p className="font-medium">{paymentResult.instructions.bank_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Account Name</Label>
                    <p className="font-medium">{paymentResult.instructions.account_name}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-gray-500 text-xs">Account Number</Label>
                      <p className="font-mono font-medium">{paymentResult.instructions.account_number}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentResult.instructions.account_number)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  {paymentResult.instructions.swift_code && (
                    <div>
                      <Label className="text-gray-500 text-xs">SWIFT Code</Label>
                      <p className="font-mono">{paymentResult.instructions.swift_code}</p>
                    </div>
                  )}
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <Label className="text-amber-800 text-xs">Reference Number (Required)</Label>
                  <div className="flex items-center justify-between">
                    <p className="font-mono font-bold text-lg">{paymentResult.instructions.reference}</p>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(paymentResult.instructions.reference)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-amber-700 mt-2">{paymentResult.instructions.message}</p>
                </div>
                <div className="text-center pt-2">
                  <p className="font-semibold text-lg">
                    Amount: {paymentResult.instructions.currency} {paymentResult.instructions.amount}
                  </p>
                </div>
              </div>
            )}

            {/* EcoCash Instructions */}
            {paymentResult.instructions?.type === 'ecocash' && (
              <div className="space-y-4">
                <h4 className="font-medium flex items-center">
                  <Smartphone className="w-5 h-5 mr-2" />
                  EcoCash Payment
                </h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-800">{paymentResult.instructions.message}</p>
                </div>
                {paymentResult.instructions.steps && (
                  <div className="space-y-2">
                    <h5 className="font-medium">Payment Steps:</h5>
                    <ol className="list-decimal list-inside space-y-1 text-gray-600">
                      {paymentResult.instructions.steps.map((step, i) => (
                        <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
                      ))}
                    </ol>
                  </div>
                )}
                <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-500 text-xs">Phone</Label>
                    <p className="font-medium">{paymentResult.instructions.phone_number}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Reference</Label>
                    <p className="font-mono text-sm">{paymentResult.instructions.reference}</p>
                  </div>
                </div>
                <div className="text-center pt-2">
                  <p className="font-semibold text-lg">
                    Amount: {paymentResult.instructions.currency} {paymentResult.instructions.amount}
                  </p>
                </div>
              </div>
            )}

            {/* Manual Payment Instructions */}
            {paymentResult.instructions?.type === 'manual' && (
              <div className="space-y-4">
                <h4 className="font-medium flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Payment Instructions
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">{paymentResult.instructions.instructions}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span>Reference Number</span>
                    <span className="font-mono font-bold">{paymentResult.instructions.reference}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span>Amount</span>
                    <span className="font-bold">{paymentResult.instructions.currency} {paymentResult.instructions.amount}</span>
                  </div>
                </div>
                {paymentResult.instructions.contact_email && (
                  <p className="text-sm text-gray-500">
                    Contact: {paymentResult.instructions.contact_email}
                  </p>
                )}
              </div>
            )}

            {/* Installment Info */}
            {paymentResult.installment_info && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium flex items-center mb-2">
                  <Calendar className="w-4 h-4 mr-2" />
                  Payment Schedule
                </h5>
                <p className="text-sm text-blue-800">
                  {paymentResult.installment_info.count} payments of {getCurrencySymbol(selectedCurrency)}{paymentResult.installment_info.amount_per_installment}
                </p>
                <p className="text-sm text-blue-700">
                  First payment due: {paymentResult.installment_info.first_payment_due}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between">
        {step === 1 && (
          <>
            <Button variant="outline" onClick={onCancel}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button onClick={() => setStep(2)} disabled={!selectedGateway}>
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Button variant="outline" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Button onClick={initializePayment} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Pay {getCurrencySymbol(selectedCurrency)}{total}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </>
        )}

        {step === 3 && (
          <Button className="w-full" onClick={handleComplete} data-testid="payment-done-btn">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Done
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default PaymentGatewaySelector;
